import React, {useState} from 'react';
import {Button, Col, DatePicker, Form, Input, Popconfirm, Row} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {internalApi} from '../../common/axios';
import {buildUrl} from '../../shared/utils/net';
import {DIALOG_KEY} from '../../shared/utils/enum';
import ConditionPanel from './conditions/ConditionPanel';
import {PushCampaignEntity} from './PushCampaignEntity';

const PushCampaignEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const data = new PushCampaignEntity(record);
    const [rows, setRows] = useState(data);
    const [conditions, setConditions] = useState(data.conditions);

    const handleSetRows = (name, value) => {
        setRows(prev => new PushCampaignEntity({...prev, [name]: value}));
    };

    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        const entity = new PushCampaignEntity({...rows, conditions});
        if (saving || entity.isInvalidate()) return;

        setSaving(true);
        toast.loading('저장 중...');
        await internalApi.post('push-campaign', {...entity.toJson(), conditions}).then(res => {
            toast.dismiss();
            toast.success(`${res.data.message} (대상 ${res.data.targetCount}명)`);
            handleClose(true);
        }).catch(err => {
            toast.dismiss();
            toast.error('저장 실패');
            console.error(err);
        }).finally(() => setSaving(false));
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}]);
    };

    const handleDelete = async () => {
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText !== 'DELETE') {
            toast.error("DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            return;
        }
        toast.loading('삭제 중...');
        await internalApi.delete(buildUrl('push-campaign', {id: rows.id})).then(res => {
            toast.dismiss();
            toast.success(res.data.message);
            handleClose(true);
        }).catch(() => {
            toast.dismiss();
            toast.error('삭제 실패');
        });
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
    };

    return (
        <Form layout="vertical" onFinish={handleSave} style={{maxHeight: '80vh', overflowY: 'auto', padding: 16}}>
            <Row gutter={16} style={{marginBottom: 16}}>
                <Col span={24}>
                    <Form.Item label="푸시 제목" required>
                        <Input
                            value={rows.title}
                            onChange={e => handleSetRows('title', e.target.value)}
                            placeholder="푸시 알림 제목"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} style={{marginBottom: 16}}>
                <Col span={24}>
                    <Form.Item label="푸시 내용" required>
                        <Input.TextArea
                            value={rows.body}
                            onChange={e => handleSetRows('body', e.target.value)}
                            placeholder="푸시 알림 내용"
                            rows={3}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} style={{marginBottom: 16}}>
                <Col span={24}>
                    <Form.Item label="발송 시각" required>
                        <DatePicker
                            value={rows.scheduledAt ? dayjs.unix(rows.scheduledAt) : null}
                            onChange={v => handleSetRows('scheduledAt', v ? v.unix() : null)}
                            showTime={{format: 'HH:mm'}}
                            format="YYYY-MM-DD HH:mm"
                            placeholder="발송 시각 선택"
                            disabledDate={d => d && d < dayjs().startOf('day')}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16} style={{marginBottom: 16}}>
                <Col span={24}>
                    <ConditionPanel conditions={conditions} onChange={setConditions}/>
                </Col>
            </Row>
            <Row gutter={16} justify="end">
                <Col>
                    {(!rows.id || rows.batchState === 'reserved') && (
                        <Button type="primary" htmlType="submit" loading={saving}>저장</Button>
                    )}
                </Col>
                <Col>
                    <Button onClick={handleClose}>취소</Button>
                </Col>
                <Col>
                    {rows.id && rows.batchState === 'reserved' && (
                        <Popconfirm
                            title="정말 삭제하시겠습니까? DELETE를 입력하세요."
                            onConfirm={handleDelete}
                            okText="삭제"
                            cancelText="취소"
                            icon={<DeleteOutlined style={{color: 'red'}}/>}
                            description={
                                <Input
                                    onChange={e => handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: e.target.value}])}
                                    placeholder="DELETE"
                                />
                            }
                        >
                            <Button danger icon={<DeleteOutlined/>}>삭제</Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
        </Form>
    );
};

export default PushCampaignEditModal;